import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getValidationProfile, runValidationProfile } from "../lib/adminApi";
import { toast } from "sonner";
import {
  ArrowLeft, Play, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
} from "lucide-react";

const STATUS_META = {
  PASS: { label: "PASS", color: "#047857", bg: "#ECFDF5", Icon: CheckCircle2 },
  WARNING: { label: "WARNING", color: "#B45309", bg: "#FFFBEB", Icon: AlertTriangle },
  FAIL: { label: "FAIL", color: "#B91C1C", bg: "#FEF2F2", Icon: XCircle },
  NOT_RUN: { label: "NOT RUN", color: "#6B7280", bg: "#F3F4F6", Icon: MinusCircle },
  NOT_CONFIGURED: { label: "NOT RUN", color: "#6B7280", bg: "#F3F4F6", Icon: MinusCircle },
};

const TYPE_LABELS = {
  greenfield: "Greenfield",
  brownfield: "Brownfield",
  cloud_migration: "Cloud Migration",
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.NOT_RUN;
  const { Icon } = m;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider rounded-sm px-2.5 py-1" style={{ color: m.color, background: m.bg }} data-testid="vp-detail-status">
      <Icon className="w-4 h-4" /> {m.label}
    </span>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="border border-[#E5E7EB] rounded-sm bg-white" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-[#F1F5F9] bg-[#F8FAFC]">
        <h3 className="text-[11px] font-bold text-[#111827] uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#F8FAFC] last:border-0">
      <span className="text-xs text-[#6B7280]">{label}</span>
      <span className="text-xs text-[#0F172A] font-medium text-right">{value || "—"}</span>
    </div>
  );
}

const FIELD_STATUS_META = {
  PASS: { color: "#047857", bg: "#ECFDF5" },
  WARNING: { color: "#B45309", bg: "#FFFBEB" },
  FAIL: { color: "#B91C1C", bg: "#FEF2F2" },
  "N/A": { color: "#6B7280", bg: "#F3F4F6" },
};

function CompareRow({ label, expected, actual, status }) {
  const m = FIELD_STATUS_META[status] || FIELD_STATUS_META["N/A"];
  return (
    <tr className={status === "WARNING" ? "bg-[#FFFBEB]" : status === "FAIL" ? "bg-[#FEF2F2]" : ""}>
      <td className="px-3 py-2 text-xs font-medium text-[#111827]">{label}</td>
      <td className="px-3 py-2 text-xs text-[#6B7280]">{expected || "—"}</td>
      <td className={`px-3 py-2 text-xs font-medium ${status === "PASS" ? "text-[#047857]" : status === "FAIL" ? "text-[#B91C1C]" : status === "WARNING" ? "text-[#B45309]" : "text-[#0F172A]"}`}>{actual || "—"}</td>
      <td className="px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5" style={{ color: m.color, background: m.bg }}>{status}</span>
      </td>
    </tr>
  );
}

export default function ValidationProfileDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setP(await getValidationProfile(id)); }
    catch { toast.error("Failed to load profile"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleRun = async () => {
    setRunning(true);
    try { const updated = await runValidationProfile(id); setP(updated); toast.success("Profile validated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="text-xs text-[#9CA3AF] py-10 text-center">Loading…</div>;
  if (!p) return <div className="text-xs text-[#9CA3AF] py-10 text-center">Profile not found.</div>;

  const isCm = p.assessment_type === "cloud_migration";
  const lr = p.latest_result || {};
  const status = lr.status || "NOT_RUN";
  const expPrimary = isCm ? p.expected_migration_strategy : p.expected_solution;

  const diffFields = new Set((lr.differences || []).map((d) => d.field));
  const primaryField = isCm ? "migration_strategy" : "solution";

  // Per-field Expected / Actual / Status rows.
  const fieldStatus = (field, expected, configuredOverride) => {
    const configured = configuredOverride !== undefined ? configuredOverride : !!expected;
    if (!lr.validated_at) return "N/A";
    if (!configured) return "N/A";
    if (diffFields.has(field)) return field === primaryField ? "FAIL" : "WARNING";
    return "PASS";
  };

  const cmApproach = lr.actual_deployment;
  const compareRows = [
    {
      label: isCm ? "Migration Strategy" : "Recommendation",
      expected: expPrimary,
      actual: isCm && cmApproach ? `${lr.actual_recommendation} · ${cmApproach}` : lr.actual_recommendation,
      status: fieldStatus(primaryField, expPrimary),
    },
    ...(!isCm ? [{
      label: "Deployment", expected: p.expected_deployment, actual: lr.actual_deployment,
      status: fieldStatus("deployment", p.expected_deployment),
    }] : []),
    { label: "Hosting", expected: p.expected_hosting, actual: lr.actual_hosting, status: fieldStatus("hosting", p.expected_hosting) },
    { label: "Complexity", expected: p.expected_complexity, actual: lr.actual_complexity, status: fieldStatus("complexity", p.expected_complexity) },
    { label: "Confidence", expected: p.expected_confidence, actual: lr.actual_confidence, status: fieldStatus("confidence", p.expected_confidence) },
  ];

  // Structured, engine-derived finding explanation (WARNING / FAIL).
  const primaryMatched = lr.validated_at && !diffFields.has(primaryField);
  const secondaryDiffs = (lr.differences || []).filter((d) => d.field !== primaryField);
  const reasonFor = (d) => {
    const site0 = (lr.engine_reasoning || [])[0] || {};
    if (d.field === "complexity") {
      const sc = site0.complexity_score;
      const scTxt = (sc !== undefined && sc !== null) ? ` (weighted complexity score ${Number(sc).toFixed(2)}; band thresholds: Low ≤ 1.50, Medium ≤ 2.20, High > 2.20)` : "";
      return `Engine computed complexity band "${d.actual}"${scTxt}; the benchmark expects "${d.expected}". The recommendation itself is unchanged.`;
    }
    if (d.field === "confidence") {
      return `Engine computed confidence band "${d.actual}"; the benchmark expects "${d.expected}". Driven by assessment completeness, recommendation margin and evidence consistency.`;
    }
    if (d.field === "hosting") return `Engine hosting "${d.actual}" differs from expected "${d.expected}".`;
    if (d.field === "deployment") return `Engine deployment "${d.actual}" differs from expected "${d.expected}".`;
    return `Field "${d.field}": expected "${d.expected}", actual "${d.actual}".`;
  };


  return (
    <div data-testid="validation-profile-detail-page">
      <button onClick={() => navigate("/admin/validation-profiles")} data-testid="vp-detail-back" className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#025979] mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Validation Profiles
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-[#025979] font-bold">{p.profile_code}</span>
            <StatusBadge status={status} />
          </div>
          <h1 className="text-xl font-bold text-[#111827] mt-1" style={{ fontFamily: "Chivo" }} data-testid="vp-detail-name">{p.profile_name}</h1>
          <p className="text-xs text-[#6B7280] mt-1">{TYPE_LABELS[p.assessment_type]} · {p.active ? "Active" : "Inactive"}</p>
        </div>
        <button onClick={handleRun} disabled={running} data-testid="vp-detail-run" className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#025979] hover:bg-[#014459] disabled:opacity-60 rounded-sm px-4 py-2.5">
          <Play className={`w-3.5 h-3.5 ${running ? "animate-pulse" : ""}`} /> {running ? "Running…" : "Run This Profile"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Scenario Overview" testid="vp-detail-overview">
          <Row label="Assessment Type" value={TYPE_LABELS[p.assessment_type]} />
          <Row label="Description" value={p.description} />
          <Row label="Business Context" value={p.business_context} />
          <Row label="Display Order" value={String(p.display_order ?? 0)} />
        </Card>

        <Card title="Expected Results" testid="vp-detail-expected">
          <Row label={isCm ? "Migration Strategy" : "Solution"} value={expPrimary} />
          {!isCm && <Row label="Deployment" value={p.expected_deployment} />}
          <Row label="Hosting" value={p.expected_hosting} />
          <Row label="Complexity" value={p.expected_complexity} />
          <Row label="Confidence" value={p.expected_confidence} />
        </Card>

        <Card title="Latest Actual Results" testid="vp-detail-actual">
          {lr.validated_at ? (
            <>
              <Row label={isCm ? "Migration Strategy" : "Recommendation"} value={lr.actual_recommendation} />
              <Row label={isCm ? "Approach" : "Deployment"} value={lr.actual_deployment} />
              <Row label="Hosting" value={lr.actual_hosting} />
              <Row label="Complexity" value={lr.actual_complexity} />
              <Row label="Confidence" value={lr.actual_confidence} />
              <Row label="Validated At" value={new Date(lr.validated_at).toLocaleString()} />
              {lr.engine_error && <Row label="Engine Error" value={lr.engine_error} />}
            </>
          ) : (
            <p className="text-xs text-[#9CA3AF]">Never validated. Click <strong>Run This Profile</strong>.</p>
          )}
        </Card>

        <Card title="Comparison" testid="vp-detail-comparison">
          {lr.validated_at ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6B7280] border-b border-[#F1F5F9]">
                  <th className="px-3 py-2 font-medium text-xs">Field</th>
                  <th className="px-3 py-2 font-medium text-xs">Expected</th>
                  <th className="px-3 py-2 font-medium text-xs">Actual</th>
                  <th className="px-3 py-2 font-medium text-xs">Status</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.filter((r) => r.expected || r.actual).map((r) => (
                  <CompareRow key={r.label} label={r.label} expected={r.expected} actual={r.actual} status={r.status} />
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-[#9CA3AF]">Run the profile to see the comparison.</p>
          )}
          {lr.validated_at && (lr.differences || []).length === 0 && (
            <p className="text-[11px] text-[#047857] mt-2">All configured values match the expected baseline.</p>
          )}
        </Card>
      </div>

      {/* Structured finding explanation (WARNING / FAIL) */}
      {lr.validated_at && (status === "WARNING" || status === "FAIL") && (
        <div className="mt-4">
          <Card title={status === "FAIL" ? "Failure Explanation" : "Warning Explanation"} testid="vp-detail-explanation">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-sm border border-[#F1F5F9] px-3 py-2">
                  <div className="text-[10px] uppercase text-[#6B7280] font-semibold">Recommendation Match</div>
                  <div className={`text-sm font-bold ${primaryMatched ? "text-[#047857]" : "text-[#B91C1C]"}`} data-testid="vp-explain-match">
                    {primaryMatched ? "Yes" : "No"}
                  </div>
                </div>
                <div className="rounded-sm border border-[#F1F5F9] px-3 py-2">
                  <div className="text-[10px] uppercase text-[#6B7280] font-semibold">{status === "FAIL" ? "Primary Difference" : "Secondary Difference"}</div>
                  <div className="text-sm font-medium text-[#0F172A]" data-testid="vp-explain-diff">
                    {status === "FAIL"
                      ? (isCm ? "Migration Strategy" : "Recommendation")
                      : (secondaryDiffs.map((d) => d.field).join(", ") || "—")}
                  </div>
                </div>
              </div>

              {status === "FAIL" && (
                <div className="rounded-sm bg-[#FEF2F2] border border-[#B91C1C]/25 px-3 py-2">
                  <div className="text-[10px] uppercase text-[#B91C1C] font-semibold mb-1">Expected vs Actual</div>
                  <div className="text-xs text-[#0F172A]">
                    Expected <strong>{expPrimary}</strong> · Actual <strong>{lr.actual_recommendation || "—"}</strong>
                  </div>
                </div>
              )}

              {(status === "WARNING" ? secondaryDiffs : (lr.differences || [])).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Reason (engine output)</div>
                  <ul className="space-y-1" data-testid="vp-explain-reasons">
                    {(status === "WARNING" ? secondaryDiffs : (lr.differences || [])).map((d, i) => (
                      <li key={i} className="text-xs text-[#0F172A]">{reasonFor(d)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Relevant scores */}
              {(lr.engine_reasoning || []).some((s) => Object.keys(s.solution_scores || {}).length || s.complexity_score != null) && (
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Relevant Scores</div>
                  {(lr.engine_reasoning || []).map((s) => (
                    <div key={s.site_key} className="mb-1.5">
                      <span className="text-[11px] text-[#025979] font-medium">{s.site_label}</span>
                      {s.complexity_score != null && (
                        <span className="text-[11px] text-[#6B7280] ml-2">complexity {Number(s.complexity_score).toFixed(2)} → {s.complexity_label}</span>
                      )}
                      {Object.keys(s.solution_scores || {}).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-0.5">
                          {Object.entries(s.solution_scores).map(([k, v]) => (
                            <span key={k} className="text-[10px] text-[#0F172A]">{k}: <strong>{typeof v === "number" ? v.toFixed(1) : v}</strong></span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Activated Triggers</div>
                  {(lr.activated_triggers || []).length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {lr.activated_triggers.map((t) => <span key={t} className="text-[10px] bg-[#EFF6FF] text-[#1E40AF] rounded px-1.5 py-0.5 font-mono">{t}</span>)}
                    </div>
                  ) : <p className="text-xs text-[#9CA3AF]">None</p>}
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Activated Capabilities</div>
                  {(lr.activated_capabilities || []).length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {lr.activated_capabilities.map((cc) => <span key={cc} className="text-[10px] bg-[#F0FDF4] text-[#166534] rounded px-1.5 py-0.5">{cc}</span>)}
                    </div>
                  ) : <p className="text-xs text-[#9CA3AF]">None</p>}
                </div>
              </div>

              {isCm && (lr.cm_key_drivers || []).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Cloud Drivers</div>
                  <ul className="list-disc list-inside text-xs text-[#0F172A] space-y-0.5">
                    {lr.cm_key_drivers.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}

              {status === "FAIL" && (lr.engine_reasoning || []).some((s) => (s.why_not || []).length) && (
                <div>
                  <div className="text-[11px] font-semibold text-[#B45309] uppercase tracking-wide mb-1">Why Not Other Solutions</div>
                  {(lr.engine_reasoning || []).map((s) => (
                    <ul key={s.site_key} className="text-[11px] text-[#6B7280] space-y-0.5">
                      {(s.why_not || []).map((w, i) => <li key={i}><strong>{w.solution}</strong>{w.score !== undefined && w.score !== null ? ` (${w.score})` : ""}: {w.reason}</li>)}
                    </ul>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Validation Reasoning */}
      <div className="mt-4">
        <Card title="Validation Reasoning" testid="vp-detail-reasoning">
          <div className="space-y-4">
            {p.expected_reason && (
              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Expected Reason</div>
                <p className="text-xs text-[#0F172A]">{p.expected_reason}</p>
              </div>
            )}

            {isCm && (lr.cm_key_drivers || []).length > 0 && (
              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Key Drivers (Engine)</div>
                <ul className="list-disc list-inside text-xs text-[#0F172A] space-y-0.5">
                  {lr.cm_key_drivers.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Activated Triggers</div>
                {(lr.activated_triggers || []).length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {lr.activated_triggers.map((t) => <span key={t} className="text-[10px] bg-[#EFF6FF] text-[#1E40AF] rounded px-1.5 py-0.5 font-mono">{t}</span>)}
                  </div>
                ) : <p className="text-xs text-[#9CA3AF]">None</p>}
              </div>
              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Activated Capabilities</div>
                {(lr.activated_capabilities || []).length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {lr.activated_capabilities.map((c) => <span key={c} className="text-[10px] bg-[#F0FDF4] text-[#166534] rounded px-1.5 py-0.5">{c}</span>)}
                  </div>
                ) : <p className="text-xs text-[#9CA3AF]">None</p>}
              </div>
            </div>

            {(lr.engine_reasoning || []).map((site) => (
              <div key={site.site_key} className="border border-[#F1F5F9] rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#111827]">{site.site_label}</span>
                  <span className="text-[10px] text-[#025979] font-medium">{site.recommendation} · {site.complexity_label}</span>
                </div>
                {(site.why || []).length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] font-semibold text-[#047857] uppercase mb-1">Why</div>
                    <ul className="list-disc list-inside text-[11px] text-[#0F172A] space-y-0.5">
                      {site.why.map((w, i) => <li key={i}>{typeof w === "string" ? w : (w.label || JSON.stringify(w))}</li>)}
                    </ul>
                  </div>
                )}
                {(site.why_not || []).length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] font-semibold text-[#B45309] uppercase mb-1">Why Not Other Solutions</div>
                    <ul className="text-[11px] text-[#6B7280] space-y-0.5">
                      {site.why_not.map((w, i) => <li key={i}><strong>{w.solution}</strong>{w.score !== undefined ? ` (${w.score})` : ""}: {w.reason}</li>)}
                    </ul>
                  </div>
                )}
                {site.solution_scores && Object.keys(site.solution_scores).length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold text-[#6B7280] uppercase mb-1">Solution Scores</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(site.solution_scores).map(([k, v]) => (
                        <span key={k} className="text-[10px] text-[#0F172A]">{k}: <strong>{typeof v === "number" ? v.toFixed(1) : v}</strong></span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
